<template>
  <v-app>
    <v-container fluid>
      <v-card class="pa-8 responsive-card">
        <!-- 功能切換按鈕 -->
        <v-row class="mb-4">
          <v-col cols="12" class="d-flex justify-center">
            <v-btn-toggle v-model="activeTab" mandatory color="teal darken-4" class="mx-auto">
              <v-btn value="reserve">預約</v-btn>
              <v-btn value="query">查詢</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <v-divider></v-divider>

        <!-- 預約介面 -->
        <v-row justify="center">
          <v-col cols="12">
            <div v-if="activeTab === 'reserve'">
              <v-form ref="reserveForm" v-if="!confirmation" v-model="valid" lazy-validation>
                <!-- 1. 長輩姓名 -->
                <v-row dense>
                  <v-col cols="12">
                    <v-text-field
                      outlined
                      label="長輩姓名"
                      v-model="reservation.elderName"
                      :rules="[(v) => !!v || '此欄位必填']"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- 2. 事件類型 -->
                <v-row dense>
                  <v-col cols="12">
                    <v-radio-group
                      dense
                      v-model="reservation.eventType"
                      row
                      :rules="[(v) => !!v || '請選擇事件']"
                      required
                    >
                      <v-radio label="外出" value="外出"></v-radio>
                      <v-radio label="訪視" value="訪視"></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>

                <!-- 3. 外出：先選擇「預計外出日期／時間」 -->
                <v-row dense v-if="reservation.eventType === '外出'">
                  <!-- 3.1 預計外出日期 -->
                  <v-col cols="12" sm="6">
                    <v-menu
                      ref="departureDateMenu"
                      v-model="departureDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          v-model="reservation.departureDate"
                          label="預計外出日期"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          :rules="[(v) => !!v || '此欄位必填']"
                          outlined
                          required
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="reservation.departureDate"
                        locale="zh-TW"
                        :min="minDate"
                        @input="
                          departureDateMenu = false
                          updateReturnMinDate()
                        "
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <!-- 3.2 預計外出時間 -->
                  <v-col cols="12" sm="6">
                    <v-select
                      dense
                      v-model="reservation.departureTime"
                      :items="availableDepartureSlots"
                      label="預計外出時間"
                      outlined
                      required
                      :rules="[(v) => !!v || '請選擇預計外出時間']"
                    ></v-select>
                  </v-col>
                  <v-col cols="12">
                    <span
                      v-if="attempted && (!reservation.departureDate || !reservation.departureTime)"
                      class="error--text"
                    >
                      請先選擇完整的預計外出日期與時間
                    </span>
                  </v-col>
                </v-row>

                <!-- 4. 外出：在「預計外出日期／時間」都已填完後，才顯示「預計回歸日期／時間」 -->
                <v-row
                  dense
                  v-if="
                    reservation.eventType === '外出' &&
                    reservation.departureDate &&
                    reservation.departureTime
                  "
                >
                  <!-- 4.1 預計回歸日期 -->
                  <v-col cols="12" sm="6">
                    <v-menu
                      ref="returnDateMenu"
                      v-model="returnDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          dense
                          v-model="reservation.returnDate"
                          label="預計回歸日期"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          :rules="[(v) => !!v || '此欄位必填']"
                          outlined
                          required
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="reservation.returnDate"
                        locale="zh-TW"
                        :min="returnMinDate"
                        @input="returnDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <!-- 4.2 預計回歸時間 -->
                  <v-col cols="12" sm="6">
                    <v-select
                      dense
                      v-model="reservation.returnTime"
                      :items="availableReturnSlots"
                      label="預計回歸時間"
                      outlined
                      required
                      :rules="[(v) => !!v || '請選擇預計回歸時間']"
                    ></v-select>
                  </v-col>
                  <v-col cols="12">
                    <span
                      v-if="attempted && (!reservation.returnDate || !reservation.returnTime)"
                      class="error--text"
                    >
                      請選擇完整的預計回歸日期與時間
                    </span>
                  </v-col>
                </v-row>

                <!-- 5. 訪視：單一日期 + 時間 -->
                <v-row dense v-else-if="reservation.eventType === '訪視'">
                  <v-col cols="12">
                    <v-menu
                      ref="dateMenu"
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="reservation.date"
                          label="預約日期"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          :rules="[(v) => !!v || '此欄位必填']"
                          outlined
                          required
                          v-bind="attrs"
                          v-on="on"
                          @change="fetchAvailableTimeSlots(reservation.date)"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="reservation.date"
                        locale="zh-TW"
                        :min="minDate"
                        @input="
                          dateMenu = false
                          fetchAvailableTimeSlots(reservation.date)
                        "
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12">
                    <div v-if="loadingTimeSlots" class="text-center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      <span class="ml-2">載入中...</span>
                    </div>
                    <v-btn-toggle
                      v-else
                      v-model="reservation.time"
                      class="my-4 d-flex flex-wrap"
                      color="purple darken-4"
                    >
                      <v-btn
                        v-for="slot in filteredTimeSlots"
                        :key="slot.slot"
                        :value="slot.slot"
                        x-large
                        :disabled="!slot.is_available"
                      >
                        {{ slot.slot }}
                        <v-tooltip bottom v-if="slot.count > 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                              mdi-information
                            </v-icon>
                          </template>
                          <span>已有 {{ slot.count }} 人預約</span>
                        </v-tooltip>
                      </v-btn>
                    </v-btn-toggle>
                    <span v-if="attempted && !reservation.time" class="error--text">
                      請選擇時間
                    </span>
                  </v-col>
                </v-row>

                <!-- 6. 主要探訪者及聯絡電話 -->
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      dense
                      outlined
                      label="主要探訪者"
                      v-model="reservation.primaryVisitor"
                      :rules="[(v) => !!v || '此欄位必填']"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      dense
                      outlined
                      label="連絡電話"
                      v-model="reservation.primaryPhone"
                      :rules="[
                        (v) => !!v || '此欄位必填',
                        (v) => /^[0-9]{10}$/.test(v) || '請輸入有效的 10 碼手機號',
                      ]"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- 7. 次要探訪者及聯絡電話 -->
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      dense
                      outlined
                      label="次要探訪者 1"
                      v-model="reservation.secondaryVisitor"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      dense
                      outlined
                      label="次要聯絡電話 1"
                      v-model="reservation.secondaryPhone"
                      :rules="[
                        (v) => v === '' || /^[0-9]{10}$/.test(v) || '請輸入有效的 10 碼手機號',
                      ]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      dense
                      outlined
                      label="次要探訪者 2"
                      v-model="reservation.secondaryVisitor2"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      dense
                      outlined
                      label="次要聯絡電話 2"
                      v-model="reservation.secondaryPhone2"
                      :rules="[
                        (v) => v === '' || /^[0-9]{10}$/.test(v) || '請輸入有效的 10 碼手機號',
                      ]"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- 8. 備註 -->
                <v-row dense v-if="reservation.eventType === '外出'">
                  <v-col cols="12">
                    <div class="mb-2">
                      <v-chip
                        v-for="preset in presetRemarks"
                        :key="preset"
                        clickable
                        @click="togglePresetRemark(preset)"
                        :outlined="!isPresetSelected(preset)"
                        :color="isPresetSelected(preset) ? 'teal lighten-2' : ''"
                        class="ma-1"
                      >
                        {{ preset }}
                      </v-chip>
                    </div>
                  </v-col></v-row
                >

                <v-row dense>
                  <v-col cols="12">
                    <v-textarea
                      outlined
                      label="備註"
                      v-model="reservation.remarks"
                      auto-grow
                    ></v-textarea>
                  </v-col>
                </v-row>

                <!-- 9. 送出按鈕 -->
                <v-row class="submit-button-row">
                  <v-col cols="12" class="text-center">
                    <v-btn
                      large
                      color="primary"
                      class="mt-2"
                      @click="submitReservation"
                      :disabled="!valid || loading"
                      :loading="loading"
                    >
                      {{ loading ? '處理中...' : '送出預約' }}
                    </v-btn>
                    <div v-if="error" class="error--text mt-2">
                      {{ error }}
                    </div>
                  </v-col>
                </v-row>
              </v-form>

              <!-- 預約確認顯示 -->
              <v-card v-else class="mt-6" elevation="0">
                <v-card-title class="text-h4 justify-center">
                  <v-icon large color="success" class="mr-2">mdi-check-circle</v-icon>
                  預約成功
                </v-card-title>
                <v-card-text class="px-6 text-subtitle-1">
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content
                        >長輩姓名：{{ confirmation.elderName }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>事件：{{ confirmation.eventType }}</v-list-item-content>
                    </v-list-item>
                    <template v-if="confirmation.eventType === '外出'">
                      <v-list-item>
                        <v-list-item-content
                          >預計外出日期：{{ confirmation.departureDate }}</v-list-item-content
                        >
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content
                          >預計外出時間：{{ confirmation.departureTime }}</v-list-item-content
                        >
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content
                          >預計回歸日期：{{ confirmation.returnDate }}</v-list-item-content
                        >
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content
                          >預計回歸時間：{{ confirmation.returnTime }}</v-list-item-content
                        >
                      </v-list-item>
                    </template>
                    <template v-else>
                      <v-list-item>
                        <v-list-item-content>日期：{{ confirmation.date }}</v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>時間：{{ confirmation.time }}</v-list-item-content>
                      </v-list-item>
                    </template>
                    <v-list-item>
                      <v-list-item-content
                        >主要探訪者：{{ confirmation.primaryVisitor }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content
                        >連絡電話：{{ confirmation.primaryPhone }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item v-if="confirmation.secondaryVisitor">
                      <v-list-item-content
                        >次要探訪者 1：{{ confirmation.secondaryVisitor }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item v-if="confirmation.secondaryPhone">
                      <v-list-item-content
                        >次要聯絡電話 1：{{ confirmation.secondaryPhone }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item v-if="confirmation.secondaryVisitor2">
                      <v-list-item-content
                        >次要探訪者 2：{{ confirmation.secondaryVisitor2 }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item v-if="confirmation.secondaryPhone2">
                      <v-list-item-content
                        >次要聯絡電話 2：{{ confirmation.secondaryPhone2 }}</v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item v-if="confirmation.remarks">
                      <v-list-item-content>備註：{{ confirmation.remarks }}</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="justify-center">
                  <v-btn dark large color="teal" @click="goBackReservation">預約下一筆</v-btn>
                </v-card-actions>
              </v-card>
            </div>

            <!-- 查詢介面 -->
            <div v-if="activeTab === 'query'">
              <v-form ref="queryForm" v-if="!queried" @submit.prevent="queryReservation">
                <v-row dense>
                  <v-col cols="12">
                    <v-radio-group
                      v-model="queryEventType"
                      row
                      :rules="[(v) => !!v || '請選擇事件類型']"
                      required
                      class="mb-4"
                    >
                      <v-radio label="外出" value="外出"></v-radio>
                      <v-radio label="訪視" value="訪視"></v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      outlined
                      label="請輸入主要聯絡電話"
                      v-model="queryPrimaryPhone"
                      :rules="[(v) => !!v || '此欄位必填']"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="text-center">
                    <v-btn
                      large
                      color="primary"
                      type="submit"
                      class="mt-4"
                      :loading="loading"
                      :disabled="loading"
                    >
                      {{ loading ? '查詢中...' : '查詢' }}
                    </v-btn>
                    <div v-if="error" class="error--text mt-2">
                      {{ error }}
                    </div>
                  </v-col>
                </v-row>
              </v-form>

              <v-card v-if="queried" class="mt-6" elevation="0">
                <v-card-title class="text-h4 justify-center"> 查詢結果 </v-card-title>
                <v-card-text class="px-6 text-subtitle-1">
                  <div v-if="queryResult && queryResult.length > 0">
                    <v-expansion-panels v-model="expandedPanel">
                      <v-expansion-panel
                        v-for="(result, index) in queryResult"
                        :key="result.id"
                        class="mb-2"
                      >
                        <v-expansion-panel-header>
                          預約 {{ index + 1 }}：{{ result.case_name }} -
                          <template v-if="queryEventType === '外出'">
                            {{ formatDateTime(result.start_time) }} 至
                            {{ formatDateTime(result.end_time) }}
                          </template>
                          <template v-else>
                            {{ formatDateTime(result.data_time) }}
                          </template>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-list dense>
                            <v-list-item>
                              <v-list-item-content
                                >長輩姓名：{{ result.case_name }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-content>事件：{{ queryEventType }}</v-list-item-content>
                            </v-list-item>
                            <template v-if="queryEventType === '外出'">
                              <v-list-item>
                                <v-list-item-content
                                  >預計外出時間：{{
                                    formatDateTime(result.start_time)
                                  }}</v-list-item-content
                                >
                              </v-list-item>
                              <v-list-item>
                                <v-list-item-content
                                  >預計回歸時間：{{
                                    formatDateTime(result.end_time)
                                  }}</v-list-item-content
                                >
                              </v-list-item>
                            </template>
                            <template v-else>
                              <v-list-item>
                                <v-list-item-content
                                  >預約時間：{{
                                    formatDateTime(result.data_time)
                                  }}</v-list-item-content
                                >
                              </v-list-item>
                            </template>
                            <v-list-item>
                              <v-list-item-content
                                >主要探訪者：{{ result.main_visitor }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-content
                                >連絡電話：{{ result.main_visitor_phone }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item v-if="result.sub_visitor_1">
                              <v-list-item-content
                                >次要探訪者 1：{{
                                  result.sub_visitor_1 || '無'
                                }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item v-if="result.sub_visitor_1_phone">
                              <v-list-item-content
                                >次要聯絡電話 1：{{
                                  result.sub_visitor_1_phone || '無'
                                }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item v-if="result.sub_visitor_2">
                              <v-list-item-content
                                >次要探訪者 2：{{
                                  result.sub_visitor_2 || '無'
                                }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item v-if="result.sub_visitor_2_phone">
                              <v-list-item-content
                                >次要聯絡電話 2：{{
                                  result.sub_visitor_2_phone || '無'
                                }}</v-list-item-content
                              >
                            </v-list-item>
                            <v-list-item v-if="result.note">
                              <v-list-item-content
                                >備註：{{ result.note || '無' }}</v-list-item-content
                              >
                            </v-list-item>
                          </v-list>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                  <div v-else class="error--text">找不到符合的預約紀錄</div>
                </v-card-text>
                <v-card-actions class="justify-center">
                  <v-btn dark large color="teal" @click="goBackQuery">返回查詢</v-btn>
                </v-card-actions>
              </v-card>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios'
import qs from 'qs'
import moment from 'moment-timezone'

export default {
  name: 'App',
  data() {
    return {
      activeTab: 'reserve',
      presetRemarks: ['停早餐', '停午餐', '停晚餐'],
      valid: false,
      attempted: false,
      dateMenu: false,
      departureDateMenu: false,
      returnDateMenu: false,
      loading: false,
      error: null,
      availableTimeSlots: [],
      loadingTimeSlots: false,
      reservation: {
        elderName: '',
        eventType: '',
        date: '',
        departureDate: '',
        returnDate: '',
        time: '',
        departureTime: '',
        returnTime: '',
        primaryVisitor: '',
        primaryPhone: '',
        secondaryVisitor: '',
        secondaryPhone: '',
        secondaryVisitor2: '',
        secondaryPhone2: '',
        remarks: '',
      },
      confirmation: null,
      reservations: [],
      queryPrimaryPhone: '',
      queryResult: null,
      queried: false,
      returnMinDate: '',
      queryEventType: '',
      expandedPanel: 0,
    }
  },
  computed: {
    defaultDate() {
      const d = new Date()
      d.setDate(d.getDate() + 2)
      return d.toISOString().substr(0, 10)
    },
    minDate() {
      return this.defaultDate
    },
    filteredTimeSlots() {
      return this.availableTimeSlots
    },
    availableDepartureSlots() {
      const slots = []
      const start = 8 * 60
      const end = 16 * 60
      for (let t = start; t <= end; t += 30) {
        const h = Math.floor(t / 60)
        const m = t % 60
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
      }
      return slots
    },
    availableReturnSlots() {
      const slots = []
      const start = 8 * 60
      const end = 20 * 60
      for (let t = start; t <= end; t += 30) {
        const h = Math.floor(t / 60)
        const m = t % 60
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
      }
      return slots
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'reserve') {
        this.resetReservation()
      } else if (newTab === 'query') {
        this.resetQuery()
        this.error = null
      }
    },
    'reservation.eventType'(newType) {
      if (newType === '訪視') {
        this.fetchAvailableTimeSlots(this.reservation.date)
      }
    },
    confirmation(val) {
      if (val === null) {
        this.reservation.date = this.defaultDate
        this.reservation.departureDate = this.defaultDate
        this.reservation.returnDate = this.defaultDate
        this.returnMinDate = this.defaultDate
      }
    },
  },
  created() {
    this.reservation.date = this.defaultDate
    this.reservation.departureDate = this.defaultDate
    this.reservation.returnDate = this.defaultDate
    this.returnMinDate = this.defaultDate
    this.fetchAvailableTimeSlots(this.defaultDate)
  },
  methods: {
    updateReturnMinDate() {
      this.returnMinDate = this.reservation.departureDate
      if (this.reservation.returnDate < this.reservation.departureDate) {
        this.reservation.returnDate = this.reservation.departureDate
      }
    },
    async submitReservation() {
      this.attempted = true
      const formValid = this.$refs.reserveForm.validate()

      if (formValid) {
        try {
          this.loading = true
          this.error = null

          let data
          let url

          if (this.reservation.eventType === '訪視') {
            const dateTime = moment(`${this.reservation.date} ${this.reservation.time}`).format()
            data = qs.stringify({
              data_time: dateTime,
              main_visitor: this.reservation.primaryVisitor,
              main_visitor_phone: this.reservation.primaryPhone,
              case_name: this.reservation.elderName,
              sub_visitor_1: this.reservation.secondaryVisitor,
              sub_visitor_1_phone: this.reservation.secondaryPhone,
              sub_visitor_2: this.reservation.secondaryVisitor2,
              sub_visitor_2_phone: this.reservation.secondaryPhone2,
              note: this.reservation.remarks,
            })
            url = '/api/standalone_reservation/visit/'
          } else if (this.reservation.eventType === '外出') {
            const startTime = moment(
              `${this.reservation.departureDate} ${this.reservation.departureTime}`
            ).format()
            const endTime = moment(
              `${this.reservation.returnDate} ${this.reservation.returnTime}`
            ).format()
            data = qs.stringify({
              start_time: startTime,
              end_time: endTime,
              case_name: this.reservation.elderName,
              main_visitor: this.reservation.primaryVisitor,
              main_visitor_phone: this.reservation.primaryPhone,
              note: this.reservation.remarks,
              sub_visitor_1: this.reservation.secondaryVisitor,
              sub_visitor_1_phone: this.reservation.secondaryPhone,
              sub_visitor_2: this.reservation.secondaryVisitor2,
              sub_visitor_2_phone: this.reservation.secondaryPhone2,
            })
            url = '/api/standalone_reservation/case_outings/'
          }

          const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: url,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            data: data,
          }

          const response = await axios.request(config)
          this.reservations.push({ ...this.reservation })
          this.confirmation = { ...this.reservation }
        } catch (error) {
          this.error = error.response?.data?.data[0] || '預約失敗，請稍後再試'
        } finally {
          this.loading = false
        }
      }
    },
    async queryReservation() {
      try {
        const formValid = this.$refs.queryForm.validate()
        if (!formValid) {
          return
        }

        this.loading = true
        this.error = null
        this.queried = true
        this.expandedPanel = 0 // 重置展開面板為第一個

        const today = moment().format('YYYY-MM-DD')
        let url

        if (this.queryEventType === '訪視') {
          url = `/api/standalone_reservation/visit/cellphone/?date=${today}&cellphone=${this.queryPrimaryPhone}`
        } else if (this.queryEventType === '外出') {
          url = `/api/standalone_reservation/case_outings/cellphone/?date=${today}&cellphone=${this.queryPrimaryPhone}`
        }

        const response = await axios.request({
          method: 'get',
          maxBodyLength: Infinity,
          url: url,
          headers: {},
        })

        if (response.data.status === 'success') {
          this.queryResult = response.data.data
        } else {
          this.error = '查無預約紀錄'
          this.queryResult = null
        }
      } catch (error) {
        console.error('查詢失敗:', error)
        this.error = error.response?.data?.message || '查詢失敗，請稍後再試'
        this.queryResult = null
      } finally {
        this.loading = false
      }
    },
    goBackReservation() {
      this.resetReservation()
    },
    goBackQuery() {
      this.resetQuery()
    },
    resetReservation() {
      this.reservation = {
        elderName: '',
        eventType: '',
        date: this.defaultDate,
        departureDate: this.defaultDate,
        returnDate: this.defaultDate,
        time: '',
        departureTime: '',
        returnTime: '',
        primaryVisitor: '',
        primaryPhone: '',
        secondaryVisitor: '',
        secondaryPhone: '',
        secondaryVisitor2: '',
        secondaryPhone2: '',
        remarks: '',
      }
      this.confirmation = null
      this.valid = false
      this.attempted = false
      this.returnMinDate = this.defaultDate
      this.availableTimeSlots = []
      if (this.$refs.reserveForm) {
        this.$refs.reserveForm.reset()
        this.$refs.reserveForm.resetValidation()
      }
    },
    resetQuery() {
      this.queryPrimaryPhone = ''
      this.queryResult = null
      this.queried = false
      if (this.$refs.queryForm) {
        this.$refs.queryForm.resetValidation()
      }
    },
    togglePresetRemark(preset) {
      const arr = this.reservation.remarks ? this.reservation.remarks.split('，') : []
      if (arr.includes(preset)) {
        this.reservation.remarks = arr.filter((item) => item !== preset).join('，')
      } else {
        arr.push(preset)
        this.reservation.remarks = arr.join('，')
      }
    },
    isPresetSelected(preset) {
      return this.reservation.remarks
        ? this.reservation.remarks.split('，').includes(preset)
        : false
    },
    async fetchAvailableTimeSlots(date) {
      try {
        this.loadingTimeSlots = true
        const response = await axios.request({
          method: 'get',
          maxBodyLength: Infinity,
          url: `/api/standalone_reservation/check_time_slot/?date_str=${date}`,
          headers: {},
        })

        if (response.data.status === 'success') {
          this.availableTimeSlots = response.data.data
        } else {
          this.error = '無法取得可用時段'
        }
      } catch (error) {
        console.error('取得時段失敗:', error)
        this.error = '取得可用時段失敗，請稍後再試'
      } finally {
        this.loadingTimeSlots = false
      }
    },
    formatDateTime(dateTimeStr) {
      return moment(dateTimeStr).format('YYYY-MM-DD HH:mm')
    },
  },
}
</script>

<style>
@media (max-width: 600px) {
  .responsive-card {
    width: 95vw !important;
  }

  .submit-button-row {
    margin-bottom: 20px !important;
  }

  .v-form {
    padding-bottom: 20px;
  }
}

@media (min-width: 601px) {
  .responsive-card {
    width: 40vw !important;
  }
}
</style>
